<template>
  <div class="teacher-selection-listing">
    <search-bar
      active
      :placeholder="searchPlaceholder"
      filter-type="dropdown"
      :enable-focus-out="true"
      @search="handleSearch"
    >
      <template #filter="{ filterChanged }">
        <school-filter
          :schools="schools"
          :current-school-code="currentUserSchoolCode"
          :disabled="disableFilter"
          @change="filterChanged"
        />
      </template>
    </search-bar>
    <data-table-list class="teacher-list" in-subpage>
      <template #selection-bar>
        <selection-bar :count="rowsSelected" @clear="handleClearSelection" />
      </template>
      <!--FIXME VUE3: CURRENTLY NOT SUPPORTED
          :mobile-breakpoint="0"
      -->
      <v-data-table-server
        v-model:page="options.page"
        v-model:sort-by="options.sortBy"
        v-model="selectedInner"
        class="_items-selectable show-first-last"
        :items-per-page-options="footerProps.itemsPerPageOptions"
        :show-current-page="footerProps.showCurrentPage"
        :headers="headers"
        :items="teachers"
        item-value="id"
        fixed-header
        :loading="loading"
        :items-per-page="options.itemsPerPage"
        show-select
        item-selectable="selectable"
        :items-length="totalRecords"
        @update:page="updatePage"
        @update:options="customSort"
      >
        <template #[`item.name`]="{ item }">
          <data-table-cell class="name" :class="{ active: sortColumn === 'name' && sortingActive }">
            <ellipsis-text :text="item.name" />
          </data-table-cell>
        </template>
        <template #[`item.email`]="{ item }">
          <data-table-cell
            class="email"
            :class="{ active: sortColumn === 'email' && sortingActive }"
          >
            <ellipsis-text :text="item.email" />
          </data-table-cell>
        </template>
        <template #[`item.school.schoolName`]="{ item }">
          <data-table-cell
            class="school"
            :class="{ active: sortColumn === 'schoolName' && sortingActive }"
          >
            <ellipsis-text :text="item.school.schoolName" />
          </data-table-cell>
        </template>
      </v-data-table-server>
    </data-table-list>
  </div>
</template>

<script setup lang="ts">
import SchoolFilter from "@plugins/teacher-selection-listing/components/SchoolFilter.vue";
import { isEmpty } from "@/utils/var.check";
import DatatableUtils from "@plugins/vuetify/service/datatable.utils";
import DeviceUtils from "@utils/device.utils";
import { ref, computed, watch, onMounted, nextTick } from "vue";
import { useI18n } from "vue-i18n";
import { useDirty } from "@plugins/common/composables/page-dirty";
import { useErrorHandler } from "@/plugins/common/composables/error";

// Props
const props = withDefaults(
  defineProps<{
    excludedUserIds?: string[];
    selectFirstAvailableSchool?: boolean;
    selectedSchoolCode?: string;
    disableFilter?: boolean;
    task?: string;
    levelAndCourses?: Record<string, unknown>[] | null;
    fetchSchools: () => Promise<unknown>;
    fetchTeachers: (criteria?: unknown) => Promise<unknown>;
  }>(),
  {
    excludedUserIds: () => [],
    selectFirstAvailableSchool: false,
    selectedSchoolCode: "",
    disableFilter: false,
    task: "",
    levelAndCourses: null,
  },
);

const emit = defineEmits(["executed"]);

// State
const loading = ref(true);
const options = ref({});
const selectedInner = ref([]);
const selected = ref([]);
const teachers = ref([]);
const schools = ref([]);
const headers = ref([]);
const footerProps = ref({});
const loadingTimeout = ref<ReturnType<typeof setTimeout> | null>(null);

const currentUserSchoolCode = ref("");
const currentUserTeacherInfoYear = ref<number | null>(null);
const totalRecords = ref(0);
const itemPageStart = ref(0);
const itemsPerPage = ref(0);
const sortColumn = ref("name");
const sortDirection = ref("ascending");
const keyword = ref("");

// Composable
const { t } = useI18n();
const dirtyId = ref("TeacherSelection");
const { setDirty, removeDirty } = useDirty(dirtyId);
const { passiveApiFailHandler } = useErrorHandler();

// Computed
const rowsSelected = computed(() => selectedInner.value.length);
const searchPlaceholder = computed(() => t("find_teachers_header_label"));
const sortingActive = computed(() => options.value.sortBy?.length > 0);

// Methods
const initDataTableOptions = function () {
  // Number of rows per page will be based on screen size
  // Small screen size - 10
  // Medium and Large screen size - 20
  itemsPerPage.value = DeviceUtils.isMobile() ? 10 : 20;
  options.value = {
    page: 1,
    itemsPerPage: itemsPerPage.value,
    sortBy: [{ key: "name", order: "asc" }],
  };
  headers.value = [
    { title: "Name", class: "name", key: "name", align: "start" },
    { title: "Email", class: "email", key: "email" },
    { title: "School", class: "school", key: "school.schoolName" },
  ];
  footerProps.value = {
    itemsPerPageOptions: [], // Override to hide
    itemsPerPageText: "", // Override to hide
    itemsPerPageAllText: "", // Override to hide
    showFirstLastPage: true,
    showCurrentPage: true,
  };
};

const fetchData = function () {
  props.fetchSchools().then((response) => {
    if (response.code !== "ok") {
      passiveApiFailHandler();
      return;
    }
    schools.value = response.data.schools ?? response.data;
    currentUserTeacherInfoYear.value = response.data.currentUserTeacherInfoYear ?? null;

    if (props.selectFirstAvailableSchool && !isEmpty(schools.value)) {
      currentUserSchoolCode.value = schools.value[0].schoolCode;
      if (isEmpty(currentUserTeacherInfoYear.value)) {
        currentUserTeacherInfoYear.value = schools.value[0].academicYear;
      }
    } else if (!isEmpty(props.selectedSchoolCode)) {
      currentUserSchoolCode.value = props.selectedSchoolCode;
      if (isEmpty(currentUserTeacherInfoYear.value)) {
        const school = schools.value.find((s) => s.schoolCode === props.selectedSchoolCode);
        currentUserTeacherInfoYear.value = school?.academicYear ?? null;
      }
    } else {
      currentUserSchoolCode.value = response.data.currentUserSchoolCode;
    }
    getTeachers();
  }, passiveApiFailHandler);
};

const getTeachers = function () {
  clearTimeout(loadingTimeout.value);
  loadingTimeout.value = null;

  if (currentUserTeacherInfoYear.value && !isEmpty(currentUserSchoolCode.value)) {
    loadingTimeout.value = setTimeout(() => {
      loading.value = true;
      loadingTimeout.value = null;
    }, 1000);

    const criteria = getSearchCriteria(props.excludedUserIds);
    props.fetchTeachers(criteria).then(
      (response) => {
        if (loadingTimeout.value) {
          clearTimeout(loadingTimeout.value);
          loadingTimeout.value = null;
        }
        totalRecords.value = response.recordsTotal ?? 0;
        teachers.value = response.data ?? [];
        loading.value = false;
      },
      () => {
        totalRecords.value = 0;
        teachers.value = [];
        loading.value = false;
        passiveApiFailHandler();
      },
    );
  }
};

const getSearchCriteria = function (excludedUserIds) {
  const options = {
    cacheable: false,
    draw: 1,
    start: itemPageStart.value,
    length: itemsPerPage.value,
    sortColumn: sortColumn.value,
    sortDirection: sortDirection.value,
    role: ["TEACHER"],
    name: keyword.value,
    excludedUserIds: excludedUserIds,
    school: currentUserSchoolCode.value,
    schools: [currentUserSchoolCode.value],
    year: currentUserTeacherInfoYear.value,
    levelAndCourses: props.levelAndCourses,
    accountStatus: "ACTIVE", // Only Active teacher should be added
    includeManageAccounts: false,
  };
  return options;
};

const updatePage = function (pageNumber: number) {
  const temp = itemPageStart.value;
  itemPageStart.value = DatatableUtils.getStartCount(pageNumber, itemsPerPage.value);
  if (temp === itemPageStart.value) {
    return;
  }
  nextTick(() => {
    getTeachers();
  });
};

const customSort = function (options) {
  if (options.sortBy?.length > 0) {
    const tempColumn = sortColumn.value;
    const tempSort = sortDirection.value;

    sortColumn.value =
      options.sortBy[0].key === "school.schoolName" ? "schoolName" : options.sortBy[0].key;
    sortDirection.value = options.sortBy[0].order === "asc" ? "ascending" : "descending";
    if (tempColumn === sortColumn.value && tempSort === sortDirection.value) {
      return;
    }
    nextTick(() => {
      getTeachers();
    });
  }
};

const handleSearch = function (search) {
  itemPageStart.value = 0;
  options.value.page = 1;
  // Default search to current school code
  currentUserSchoolCode.value =
    search.filter.schoolCode === "" ? currentUserSchoolCode.value : search.filter.schoolCode;
  keyword.value = search.keyword;

  nextTick(() => {
    getTeachers();
  });
};

const handleClearSelection = function () {
  selectedInner.value = [];
};

const handleDirtyData = function () {
  if (selectedInner.value.length === 0) {
    removeDirty(dirtyId.value);
  } else {
    setDirty(dirtyId.value);
  }
};

// Watch
watch(
  () => props.task,
  (newTask) => {
    if (newTask === "getSelectedTeacher") {
      emit("executed", { code: "ok", data: selected.value, errors: [] });
    }
  },
);

watch(
  selectedInner,
  () => {
    handleDirtyData();
    DatatableUtils.filterSelected(selectedInner.value, teachers.value, "id", selected.value);
  },
  { deep: true },
);

// Mounted
onMounted(() => {
  initDataTableOptions();
  fetchData();
});
</script>

<style lang="scss">
@import "@styles/layout/breakpoint";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.teacher-selection-listing {
  .title {
    margin-bottom: $spacing-05;
    @include carbon--type-style("productive-heading-02", true);
  }

  // Cannot put this styling in SearchBar global component as there are places
  // where the search bar is standalone or background of the container is not pale-blue
  .search-bar-wrapper {
    @include media("tablet-min") {
      padding: $spacing-03;
    }
    background-color: $ui-01;
    border-radius: 10px 10px 0 0;
  }
  .search-bar {
    justify-content: flex-end;
    @include media("mobile") {
      display: flex;
      flex-flow: row wrap;
      > .bx--form-item {
        padding: $spacing-03;
      }
      .search-toggle {
        // To prevent jumping behavior when toggling the search
        margin: $spacing-05;
      }
    }

    > .bx--form-item {
      padding-right: $spacing-03;
    }

    .school-filter {
      margin-left: $spacing-03;
      flex: 0 0 20rem;

      @include media("mobile") {
        flex: 0 0 100%;
        padding: $spacing-03;
        border-top: 1px solid $ui-03;
        border-bottom: 1px solid $ui-03;
      }

      .bx--list-box {
        background-color: $field-02;

        &:hover {
          background-color: $hover-ui;
          .bx--text-input {
            background-color: $hover-ui;
          }
        }
      }

      .bx--text-input {
        background-color: $field-02;
      }
    }

    .bx--search-input {
      height: 2.5rem;
      border: 1px solid $ui-03;
      background-color: $field-02;
    }
  }

  .data-table-list {
    > .v-table {
      > .v-table__wrapper {
        border-radius: 0;
      }
    }

    .selection-bar {
      border-radius: 0;
      &._active {
        border-radius: 0;
      }
    }
  }
}
</style>
